import { calculateBessRevenueStreams } from './bessCalculations';

// --- Enhanced Financial Calculation Engine with BESS ---
export const calculateFinancials = (inputs) => {
    
    // Input validation and edge case protection
    const validateInputs = (inputs) => {
        const validated = { ...inputs };
        
        // Prevent division by zero
        validated.depreciationPeriod = Math.max(1, validated.depreciationPeriod);
        validated.operationPeriod = Math.max(1, validated.operationPeriod);
        validated.bessReplacementPeriod = Math.max(1, validated.bessReplacementPeriod);
        
        // Prevent invalid percentages
        validated.pvDegradationRate = Math.max(0, Math.min(0.5, validated.pvDegradationRate)); // Max 50% degradation
        validated.bessDegradationRate = Math.max(0, Math.min(0.5, validated.bessDegradationRate)); // Max 50% degradation
        validated.bessRoundTripEfficiency = Math.max(0.1, Math.min(1, validated.bessRoundTripEfficiency)); // 10%-100%
        validated.salvageValueRate = Math.max(0, Math.min(0.5, validated.salvageValueRate)); // 0%-50%
        
        // Prevent negative values where inappropriate
        validated.installedCapacity = Math.max(0.1, validated.installedCapacity);
        validated.bessEnergyCapacity = Math.max(0, validated.bessEnergyCapacity);
        validated.bessPowerCapacity = Math.max(0, validated.bessPowerCapacity);
        validated.onGridTariff = Math.max(0.001, validated.onGridTariff); // Minimum $0.001/kWh
        
        // Add additional bounds validation
        validated.annualHours = Math.max(0, Math.min(8760, validated.annualHours));
        validated.constructionPeriod = Math.max(1, Math.min(10, validated.constructionPeriod));
        validated.investmentPerMW = Math.max(1000, Math.min(10000000, validated.investmentPerMW));
        validated.longTermLoanRate = Math.max(0, Math.min(0.5, validated.longTermLoanRate));
        validated.discountRate = Math.max(0.01, Math.min(0.5, validated.discountRate));
        
        return validated;
    };
    
    const validInputs = validateInputs(inputs);

    const calculateInvestmentAndFinancing = () => {
        const solarInvestment = validInputs.installedCapacity * validInputs.investmentPerMW;
        
        // BESS Investment Calculation
        let bessInvestment = 0;
        if (validInputs.bessEnabled) {
            bessInvestment = (validInputs.bessEnergyCapacity * validInputs.bessCostPerMWh) + 
                           (validInputs.bessPowerCapacity * validInputs.bessPowerCostPerMW);
        }
        
        const totalInvestmentEstimate = solarInvestment + bessInvestment;
        const capitalFundRatio = totalInvestmentEstimate > 0 ? validInputs.ownFunds / totalInvestmentEstimate : 0;
        const loanRatio = 1 - capitalFundRatio;
        const constructionLoan = totalInvestmentEstimate * loanRatio;

        const equipmentPurchase = totalInvestmentEstimate * 0.7;
        const installationProject = totalInvestmentEstimate * 0.15;
        const otherConstructionCosts = totalInvestmentEstimate * 0.1;
        const preparatoryWork = totalInvestmentEstimate * 0.05;
        const totalStaticInvestment = equipmentPurchase + installationProject + otherConstructionCosts + preparatoryWork;
        const interestDuringConstruction = constructionLoan * validInputs.longTermLoanRate * validInputs.constructionPeriod;
        const workingCapital = totalInvestmentEstimate * 0.02;
        const totalInvestment = totalStaticInvestment + interestDuringConstruction + workingCapital;
        
        const capital = validInputs.ownFunds;
        const loan = totalInvestment - capital;
        const totalFinancing = capital + loan;

        return {
            totalInvestment,
            totalStaticInvestment,
            workingCapital,
            financing: { capital, loan, totalFinancing },
            loanDetails: { constructionLoan, capitalFundRatio },
            interestDuringConstruction,
        solarInvestment,
            bessInvestment
        };
    };

    const calculateLoanRepayment = (loanAmount, loanRate, term) => {
        const schedule = [];
        let remainingLoan = loanAmount;
        const annualPrincipalPayment = term > 0 ? loanAmount / term : 0;

        for (let year = 1; year <= term; year++) {
            if (remainingLoan <= 0.01) {
                schedule.push({ year, principal: 0, interest: 0, totalPayment: 0, remainingLoan: 0 });
                continue;
            }
            const interestPayment = remainingLoan * loanRate;
            const principalPayment = Math.min(remainingLoan, annualPrincipalPayment);
            const totalPayment = principalPayment + interestPayment;
            remainingLoan -= principalPayment;
            schedule.push({ year, principal: principalPayment, interest: interestPayment, totalPayment, remainingLoan });
        }
        return schedule;
    };
    
    const calculateIndicators = (projectCF, capitalCF, discountRate, totalInvestment, costStatement, pnlStatement, depreciableBase) => {
        const calculateIRR = (cashflows, maxIterations = 100, tolerance = 1e-7) => {
            // Edge case: all positive or all negative cash flows
            const hasPositive = cashflows.some(cf => cf > 0);
            const hasNegative = cashflows.some(cf => cf < 0);
            if (!hasPositive || !hasNegative) return 0; // No IRR exists
            
            // Edge case: no initial investment
            if (cashflows[0] >= 0) return 0;
            
            let guess = 0.1;
            let oscillationCount = 0;
            let lastGuess = guess;
            
            for (let i = 0; i < maxIterations; i++) {
                const currentGuess = guess;
                const npv = cashflows.reduce((acc, val, j) => acc + val / Math.pow(1 + currentGuess, j), 0);
                const derivative = cashflows.reduce((acc, val, j) => acc - j * val / Math.pow(1 + currentGuess, j + 1), 0);
                
                // Edge case: derivative too small (near zero)
                if (Math.abs(derivative) < tolerance) break;
                
                const newGuess = guess - npv / derivative;
                
                // Edge case: invalid calculation results
                if (isNaN(newGuess) || !isFinite(newGuess)) break;
                
                // Edge case: extremely negative rates (project failure)
                if (newGuess < -0.99) return -0.99;
                
                // Check for oscillation
                if (Math.abs(newGuess - lastGuess) < tolerance * 10) {
                    oscillationCount++;
                    if (oscillationCount > 3) return (newGuess + lastGuess) / 2;
                } else {
                    oscillationCount = 0;
                }
                
                // Convergence check
                if (Math.abs(newGuess - guess) < tolerance) return newGuess;
                
                lastGuess = guess;
                guess = newGuess;
            }
            return isNaN(guess) || !isFinite(guess) ? 0 : Math.max(guess, -0.99);
        };

        const projectNPV = projectCF.reduce((acc, val, i) => acc + val / Math.pow(1 + discountRate, i), 0);
        const capitalNPV = capitalCF.reduce((acc, val, i) => acc + val / Math.pow(1 + discountRate, i), 0);
        const projectIRR = calculateIRR(projectCF);
        const capitalIRR = calculateIRR(capitalCF);

        let cumulativeCF = 0;
        let paybackPeriod = Infinity;
        for(let i = 1; i < projectCF.length; i++) {
            cumulativeCF += projectCF[i];
            if(cumulativeCF >= -projectCF[0]) {
                paybackPeriod = (i - 1) + (-projectCF[0] - (cumulativeCF - projectCF[i])) / projectCF[i];
                break;
            }
        }
        
        let cumulativeDiscountedCF = 0;
        let discountedPaybackPeriod = Infinity;
        for(let i = 1; i < projectCF.length; i++) {
            const discountedCF = projectCF[i] / Math.pow(1 + discountRate, i);
            cumulativeDiscountedCF += discountedCF;
            if(cumulativeDiscountedCF >= -projectCF[0]) {
                 const prevCumulativeDiscountedCF = cumulativeDiscountedCF - discountedCF;
                 discountedPaybackPeriod = (i - 1) + (-projectCF[0] - prevCumulativeDiscountedCF) / discountedCF;
                 break;
            }
        }

        // Enhanced LCOE Calculation including BESS (O&M + CapEx)
        const sumDiscountedOandM = costStatement.reduce((acc, cost) => {
            return acc + (cost.operatingCost + cost.landCost + (cost.bessOperatingCost || 0)) / Math.pow(1 + discountRate, cost.year);
        }, 0);
        
        // Include BESS replacement CapEx in LCOE calculation
        const sumDiscountedBessCapex = bessStatement.reduce((acc, bess) => {
            return acc + (bess.replacementCapex || 0) / Math.pow(1 + discountRate, bess.year);
        }, 0);
        
        const salvageValue = depreciableBase * validInputs.salvageValueRate;
        const discountedSalvageValue = salvageValue / Math.pow(1 + discountRate, validInputs.operationPeriod);

        const totalDiscountedCosts = totalInvestment + sumDiscountedOandM + sumDiscountedBessCapex - discountedSalvageValue;

        const discountedEnergy = pnlStatement.reduce((acc, pnl) => {
            return acc + pnl.annualGeneration / Math.pow(1 + discountRate, pnl.year);
        }, 0);

        const lcoe = discountedEnergy > 0 ? totalDiscountedCosts / discountedEnergy : 0;

        return { projectNPV, capitalNPV, projectIRR, capitalIRR, paybackPeriod, discountedPaybackPeriod, lcoe };
    };

    const outputs = {};
    const { totalInvestment, totalStaticInvestment, workingCapital, financing, loanDetails, interestDuringConstruction, solarInvestment, bessInvestment } = calculateInvestmentAndFinancing();
    outputs.investmentPlan = { totalInvestment, totalStaticInvestment, workingCapital, ...loanDetails, interestDuringConstruction, solarInvestment, bessInvestment };
    outputs.financing = financing;

    const depreciableBase = totalStaticInvestment + interestDuringConstruction;
    const annualDepreciation = validInputs.depreciationPeriod > 0 ? (depreciableBase * (1 - validInputs.salvageValueRate)) / validInputs.depreciationPeriod : 0;
    outputs.loanRepaymentSchedule = calculateLoanRepayment(financing.loan, validInputs.longTermLoanRate, validInputs.operationPeriod);

    // BESS Asset Tracking for proper depreciation of replacements
    const bessAssets = [];
    if (validInputs.bessEnabled) {
        // Initial BESS asset
        bessAssets.push({
            installYear: 0,
            cost: bessInvestment,
            depreciationPeriod: validInputs.bessReplacementPeriod,
            annualDepreciation: bessInvestment / validInputs.bessReplacementPeriod
        });
    }

    const pnlStatement = [], costStatement = [], cashFlowStatement = [], balanceSheet = [], evaStatement = [], bessStatement = [];
    let accumulatedRetainedEarnings = 0;
    let accumulatedStatutoryReserve = 0;
    let accumulatedDiscretionaryReserve = 0;

    for (let year = 1; year <= validInputs.operationPeriod; year++) {
        const pnl = {}, cost = {}, cf = {}, balance = {}, eva = {}, bess = {};
        const degradationFactor = Math.pow((1 - validInputs.pvDegradationRate), year - 1);
        const annualGeneration = (validInputs.installedCapacity * 1000 * validInputs.annualHours) * degradationFactor;
        const loanInterest = (outputs.loanRepaymentSchedule && outputs.loanRepaymentSchedule[year - 1]) ? outputs.loanRepaymentSchedule[year - 1].interest : 0;

        // BESS Degradation and Replacement
        let bessDegradationFactor = 1;
        let bessReplacementCapex = 0;
        
        if (validInputs.bessEnabled) {
            bessDegradationFactor = Math.pow((1 - validInputs.bessDegradationRate), year - 1);
            
            // BESS replacement schedule - treat as CapEx, not OpEx
            if (year > 0 && year % validInputs.bessReplacementPeriod === 0 && year < validInputs.operationPeriod) {
                const replacementCost = bessInvestment * validInputs.bessReplacementCostFactor;
                bessReplacementCapex = replacementCost;
                
                // Add new BESS asset for depreciation tracking
                bessAssets.push({
                    installYear: year,
                    cost: replacementCost,
                    depreciationPeriod: validInputs.bessReplacementPeriod,
                    annualDepreciation: replacementCost / validInputs.bessReplacementPeriod
                });
            }
        }

        pnl.year = year;
        pnl.annualGeneration = annualGeneration;
        pnl.operatingIncome = annualGeneration * validInputs.onGridTariff;
        
        // BESS Revenue Streams (with proper degradation by revenue type)
        const bessRevenues = calculateBessRevenueStreams(validInputs, annualGeneration);
        if (validInputs.bessEnabled) {
            // Energy-based revenues degrade, power-based revenues don't
            const degradedArbitrage = bessRevenues.arbitrageRevenue * bessDegradationFactor;
            const degradedAncillary = bessRevenues.ancillaryRevenue * bessDegradationFactor;
            const stableCapacity = bessRevenues.capacityRevenue; // No degradation
            const stableDemandReduction = bessRevenues.demandChargeReduction; // No degradation
            pnl.bessRevenue = degradedArbitrage + degradedAncillary + stableCapacity + stableDemandReduction;
        } else {
            pnl.bessRevenue = 0;
        }
        pnl.totalRevenue = pnl.operatingIncome + pnl.bessRevenue;
        
        cost.year = year;
        
        // Calculate land costs (annual lease/rent)
        const totalAcres = validInputs.installedCapacity * validInputs.acresPerMW;
        const annualLandCost = totalAcres * validInputs.landCostPerAcre;
        
        if (year === 1) {
            cost.operatingCost = pnl.operatingIncome * validInputs.operatingCostRate;
        } else {
            const previousYearCost = costStatement[costStatement.length - 1].operatingCost;
            cost.operatingCost = previousYearCost * (1 + validInputs.operatingCostEscalationRate);
        }
        
        cost.landCost = annualLandCost;

        // BESS Operating Costs (pure O&M only, no CapEx)
        cost.bessOperatingCost = 0;
        if (validInputs.bessEnabled) {
            cost.bessOperatingCost = validInputs.bessPowerCapacity * validInputs.bessOperatingCostRate;
        }

        // Solar Depreciation
        cost.depreciation = year <= validInputs.depreciationPeriod ? annualDepreciation : 0;
        
        // BESS Depreciation (multiple assets with staggered replacement)
        cost.bessDepreciation = 0;
        if (validInputs.bessEnabled) {
            cost.bessDepreciation = bessAssets.reduce((total, asset) => {
                const assetAge = year - asset.installYear;
                if (assetAge > 0 && assetAge <= asset.depreciationPeriod) {
                    return total + asset.annualDepreciation;
                }
                return total;
            }, 0);
        }
        cost.interest = loanInterest;
        cost.totalCost = cost.operatingCost + cost.depreciation + cost.bessDepreciation + cost.interest + cost.landCost + cost.bessOperatingCost;
        cost.costPerKwh = annualGeneration > 0 ? cost.totalCost / annualGeneration : 0;
        
        pnl.vatSurtax = pnl.totalRevenue * validInputs.vatRate * validInputs.surtaxRate;
        pnl.totalProfit = pnl.totalRevenue - cost.totalCost - pnl.vatSurtax;
        pnl.incomeTax = pnl.totalProfit > 0 ? pnl.totalProfit * validInputs.incomeTaxRate : 0;
        pnl.netProfit = pnl.totalProfit - pnl.incomeTax;
        
        const statutoryReserve = pnl.netProfit > 0 ? pnl.netProfit * validInputs.statutoryReserveRate : 0;
        const discretionaryReserve = pnl.netProfit > 0 ? pnl.netProfit * validInputs.discretionaryReserveRate : 0;
        const profitAvailableForDistribution = pnl.netProfit - statutoryReserve - discretionaryReserve;
        pnl.profitDistribution = profitAvailableForDistribution > 0 ? profitAvailableForDistribution * validInputs.profitDistributionRate : 0;
        pnl.retainedEarnings = profitAvailableForDistribution - pnl.profitDistribution;
        
        accumulatedStatutoryReserve += statutoryReserve;
        accumulatedDiscretionaryReserve += discretionaryReserve;
        accumulatedRetainedEarnings += pnl.retainedEarnings;

        // BESS Performance Tracking
        bess.year = year;
        bess.energyCapacity = validInputs.bessEnabled ? validInputs.bessEnergyCapacity * bessDegradationFactor : 0;
        bess.powerCapacity = validInputs.bessEnabled ? validInputs.bessPowerCapacity : 0;
        // Energy-based revenues degrade with battery capacity
        bess.arbitrageRevenue = bessRevenues.arbitrageRevenue * bessDegradationFactor;
        bess.ancillaryRevenue = bessRevenues.ancillaryRevenue * bessDegradationFactor;
        // Power-based revenues don't degrade significantly (MW capacity vs MWh capacity)
        bess.capacityRevenue = bessRevenues.capacityRevenue;
        bess.demandChargeReduction = bessRevenues.demandChargeReduction;
        bess.operatingCost = cost.bessOperatingCost;
        bess.replacementCapex = bessReplacementCapex; // Now tracked as CapEx
        bess.bessDepreciation = cost.bessDepreciation;
        bess.degradationFactor = bessDegradationFactor;

        cf.year = year;
        cf.netCashFlow_op = pnl.netProfit + cost.depreciation + cost.bessDepreciation;
        
        balance.year = year;
        
        // Calculate total fixed assets including all BESS assets
        const solarFixedAssets = Math.max(0, depreciableBase - (annualDepreciation * Math.min(year, validInputs.depreciationPeriod)));
        const bessFixedAssets = bessAssets.reduce((total, asset) => {
            const assetAge = year - asset.installYear;
            if (assetAge >= 0) {
                const accumulatedDepreciation = Math.min(assetAge, asset.depreciationPeriod) * asset.annualDepreciation;
                return total + Math.max(0, asset.cost - accumulatedDepreciation);
            }
            return total;
        }, 0);
        const fixedAssets = solarFixedAssets + bessFixedAssets;
        
        const cashFromOps = (balanceSheet[year-2]?.currentAssets || workingCapital) + cf.netCashFlow_op;
        const financingActivities = - ((outputs.loanRepaymentSchedule && outputs.loanRepaymentSchedule[year-1]) ? outputs.loanRepaymentSchedule[year-1].principal : 0) - pnl.profitDistribution - bessReplacementCapex;
        balance.currentAssets = cashFromOps + financingActivities;
        balance.totalAssets = fixedAssets + balance.currentAssets;
        
        balance.longTermDebt = (outputs.loanRepaymentSchedule && outputs.loanRepaymentSchedule[year - 1]) ? outputs.loanRepaymentSchedule[year - 1].remainingLoan : 0;
        balance.currentLiabilities = 0; 
        balance.totalLiabilities = balance.longTermDebt + balance.currentLiabilities;
        
        balance.equity = financing.capital;
        balance.accumulatedProfit = accumulatedRetainedEarnings;
        balance.totalEquity = balance.equity + accumulatedRetainedEarnings + accumulatedStatutoryReserve + accumulatedDiscretionaryReserve;
        balance.totalLiabilitiesAndEquity = balance.totalLiabilities + balance.totalEquity;
        balance.check = balance.totalAssets - balance.totalLiabilitiesAndEquity;

        const nopat = pnl.netProfit + loanInterest * (1 - validInputs.incomeTaxRate);
        eva.year = year;
        const capitalEmployed = (balanceSheet[year-2]?.totalAssets || totalInvestment) - (balanceSheet[year-2]?.currentLiabilities || 0);
        eva.eva = nopat - capitalEmployed * validInputs.discountRate;

        pnlStatement.push(pnl); costStatement.push(cost); cashFlowStatement.push(cf); balanceSheet.push(balance); evaStatement.push(eva); bessStatement.push(bess);
    }
    
    outputs.pnlStatement = pnlStatement; outputs.costStatement = costStatement; outputs.cashFlowStatement = cashFlowStatement; outputs.balanceSheet = balanceSheet; outputs.evaStatement = evaStatement; outputs.bessStatement = bessStatement;
    
    const projectCF = [-totalInvestment, ...pnlStatement.map(p => {
        const costForYear = costStatement.find(c=>c.year===p.year);
        return p.netProfit + (p.interest || 0) * (1 - validInputs.incomeTaxRate) + (costForYear?.depreciation || 0) + (costForYear?.bessDepreciation || 0);
    })];
    const capitalCF = [-financing.capital, ...pnlStatement.map(p => p.profitDistribution)];
    outputs.indicators = calculateIndicators(projectCF, capitalCF, validInputs.discountRate, totalInvestment, costStatement, pnlStatement, depreciableBase);
    
    return outputs;
};